import {type ChangeEventHandler, memo, useState} from 'react';
import {useSelector} from 'react-redux';
import {selectEventOptions} from '~/common/fabric/reducers/eviteEvent/selectors';
import {GuestlistOverlayHeader} from '~/ViewInvitation/components/Guestlist/GuestlistOverlayHeader';
import {Input} from '~library/atoms/Input';
import {Select} from '~library/atoms/Select';
import {Typography} from '~library/atoms/Typography';

import * as styles from './RsvpSettings.module.scss';

interface OptionToggleProps {
  value: boolean;
  onChange: (value: boolean) => void;
  label: string;
  description: string;
  'data-qa-id': string;
}

const OptionToggle = memo(
  ({value, onChange, label, description, 'data-qa-id': dataQaId}: OptionToggleProps) => {
    return (
      <div className={styles.optionWrapper}>
        <Typography variant="paragraph2">{label}</Typography>
        <div className={styles.optionValueWrapper}>
          <Typography variant="paragraph3" color="pebble">
            {description}
          </Typography>
          <Input
            data-qa-id={dataQaId}
            type="toggle"
            value={value}
            onChange={() => onChange(!value)}
          />
        </div>
      </div>
    );
  }
);

OptionToggle.displayName = 'OptionToggle';

interface OptionDropdownProps {
  value: number | undefined;
  onChange: (value: number | undefined) => void;
  label: string;
  description: string;
  'data-qa-id': string;
}

const OptionDropdown = memo(
  ({label, description, onChange, value, 'data-qa-id': dataQaId}: OptionDropdownProps) => {
    const updateValue: ChangeEventHandler<HTMLSelectElement> = (e) =>
      onChange(Number(e.target.value) || undefined);
    return (
      <div className={styles.optionWrapper}>
        <Typography variant="paragraph2">{label}</Typography>
        <div className={styles.optionValueWrapper}>
          <Typography variant="paragraph3" color="pebble">
            {description}
          </Typography>
          <Select
            id={dataQaId}
            data-qa-id={dataQaId}
            value={value}
            onChange={updateValue}
            options={[]}
            name={dataQaId}
          />
        </div>
      </div>
    );
  }
);

OptionDropdown.displayName = 'OptionDropdown';

interface OptionInputProps {
  value: number | undefined;
  onChange: (value: number | undefined) => void;
  label: string;
  description: string;
  'data-qa-id': string;
}

const OptionInput = memo(
  ({label, description, onChange, value, 'data-qa-id': dataQaId}: OptionInputProps) => {
    const updateValue: ChangeEventHandler<HTMLInputElement> = (e) =>
      onChange(Number(e.target.value) || undefined);
    return (
      <div className={styles.optionWrapper}>
        <Typography variant="paragraph2">{label}</Typography>
        <div className={styles.optionValueWrapper}>
          <Typography variant="paragraph3" color="pebble">
            {description}
          </Typography>
          <Input type="number" value={value} onChange={updateValue} data-qa-id={dataQaId} />
        </div>
      </div>
    );
  }
);

OptionInput.displayName = 'OptionInput';

interface RsvpSettingsProps {
  onClose: () => void;
}

interface FormState {
  isPrivateGuestlist: boolean;
  allowMaybeRsvps: boolean;
  allowKids: boolean;
  maxEventCapacity: number | undefined;
  plusOnes: number | undefined;
}

export const RsvpSettings = memo(({onClose}: RsvpSettingsProps) => {
  const options = useSelector(selectEventOptions);

  /* TODO: this will be added in the future
    setHideGuestlist(newOptions.event_option_private_guestlist);
    setAllowMaybeRSVPs(newOptions.event_option_allow_maybe_rsvps);
    setAllowKids(newOptions.event_option_allow_kids);
    setLimitPerGuest(newOptions.event_option_max_guest_number);
    setAllowBringPeople(newOptions.event_option_allow_guest_number);

    setMaxGuestList(newOptions.event_option_max_guests);
    setShowMaxGuest(!!newOptions.event_option_max_guests);
   */
  const [formState, setFormState] = useState<FormState>({
    isPrivateGuestlist: options.event_option_private_guestlist,
    allowMaybeRsvps: options.event_option_allow_maybe_rsvps,
    allowKids: options.event_option_allow_kids,
    plusOnes: options.event_option_allow_guest_number,
    maxEventCapacity: options.event_option_max_guest_number,
  });

  return (
    <div>
      <GuestlistOverlayHeader onClose={onClose} goBack={onClose} />
      <div>
        <OptionToggle
          onChange={(value) => setFormState({...formState, isPrivateGuestlist: value})}
          value={formState.isPrivateGuestlist}
          label="Private guest list"
          description="Only the host can see the guest list."
          data-qa-id="private-guestlist-toggle"
        />
        <OptionToggle
          onChange={(value) => setFormState({...formState, allowMaybeRsvps: value})}
          value={formState.allowMaybeRsvps}
          label="Maybe RSVPs"
          description={`Let guests RSVP "Maybe" if they aren't ready to commit yet.`}
          data-qa-id="maybe-rsvp-toggle"
        />
        <OptionToggle
          onChange={(value) => setFormState({...formState, allowKids: value})}
          value={formState.allowKids}
          label="Family headcount"
          description="Prompt guests to specify how many adults and kids are attending."
          data-qa-id="family-headcount-toggle"
        />
        <OptionDropdown
          data-qa-id="plus-ones-dropdown"
          onChange={(value) => setFormState({...formState, plusOnes: value})}
          value={formState.plusOnes}
          label="Plus ones"
          description="Specify how many additional people each guest can bring."
        />
        <OptionInput
          onChange={(value) => setFormState({...formState, maxEventCapacity: value})}
          value={formState.maxEventCapacity}
          label="Max event capacity"
          description="Limit the total number of guests and plus ones who can RSVP “Yes” to your event."
          data-qa-id="max-event-capacity-input"
        />
      </div>
    </div>
  );
});

RsvpSettings.displayName = 'RsvpSettings';
